import type {
  AppEdge,
  AppNode,
  NodeKind,
} from '../components/fluxograma/types'
import type { QualityFlowchartGraph } from './qualityFlowchart'

export type FlowchartAiMessageRole = 'user' | 'assistant'

const NODE_KINDS: NodeKind[] = [
  'startEnd',
  'process',
  'decision',
  'inputOutput',
  'annotation',
  'group',
]

export interface FlowchartAiChat {
  id: string
  flowchart_id: string
  profile_id: string
  tess_root_id: number | string | null
  subject: string
  created_at: string
  updated_at?: string
}

export interface FlowchartAiMessage {
  id: string
  chat_id: string
  role: FlowchartAiMessageRole
  content: string
  created_at: string
}

export type FlowchartAiCreateAction = 'create_flowchart_chat'
export type FlowchartAiEditAction = 'edit_flowchart'

export interface FlowchartAiCreatePayload {
  action: FlowchartAiCreateAction
  profile_id: string
  flowchart_id: string
  title: string
  graph: QualityFlowchartGraph
}

export interface FlowchartAiEditPayload {
  action: FlowchartAiEditAction
  profile_id: string
  flowchart_id: string
  chat_id: string
  tess_root_id?: number | string | null
  message: string
  graph: QualityFlowchartGraph
}

export interface FlowchartAiWebhookResult {
  reply?: string
  chat_id?: string
  tess_root_id?: number | string | null
  root_id?: number | string | null
  graph?: QualityFlowchartGraph | null
}

export type ValidatedFlowGraph = {
  nodes: AppNode[]
  edges: AppEdge[]
  viewport: QualityFlowchartGraph['viewport']
  edgeType: QualityFlowchartGraph['edgeType']
}

const NODE_KIND_SET = new Set<string>(NODE_KINDS)

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Valida o grafo retornado pela Tess/n8n antes de aplicar no editor.
 * Retorna null se inválido.
 */
export function validateFlowGraphFromAi(
  raw: unknown
): ValidatedFlowGraph | null {
  if (!isRecord(raw)) return null

  const nodesRaw = raw.nodes
  const edgesRaw = raw.edges
  if (!Array.isArray(nodesRaw) || !Array.isArray(edgesRaw)) return null

  const nodeIds = new Set<string>()
  const nodes: AppNode[] = []

  for (const item of nodesRaw) {
    if (!isRecord(item)) return null
    const id = typeof item.id === 'string' ? item.id.trim() : ''
    if (!id || nodeIds.has(id)) return null
    nodeIds.add(id)

    const type = typeof item.type === 'string' ? item.type : 'process'
    if (!NODE_KIND_SET.has(type)) return null

    const position = item.position
    if (!isRecord(position) || !isFiniteNumber(position.x) || !isFiniteNumber(position.y)) {
      return null
    }

    const data = isRecord(item.data) ? item.data : {}
    const label =
      typeof data.label === 'string' ? data.label : String(data.label ?? '')

    nodes.push({
      id,
      type: type as NodeKind,
      position: { x: position.x, y: position.y },
      parentId: typeof item.parentId === 'string' ? item.parentId : undefined,
      width: isFiniteNumber(item.width) ? item.width : undefined,
      height: isFiniteNumber(item.height) ? item.height : undefined,
      extent: item.extent === 'parent' ? 'parent' : undefined,
      expandParent: item.expandParent === true ? true : undefined,
      style: isRecord(item.style) ? (item.style as AppNode['style']) : undefined,
      data: {
        label,
        color: typeof data.color === 'string' ? data.color : undefined,
      },
    })
  }

  // parentId deve referenciar um nó existente
  for (const node of nodes) {
    if (node.parentId && !nodeIds.has(node.parentId)) return null
  }

  const edgeIds = new Set<string>()
  const edges: AppEdge[] = []

  for (const item of edgesRaw) {
    if (!isRecord(item)) return null
    const id = typeof item.id === 'string' ? item.id.trim() : ''
    const source = typeof item.source === 'string' ? item.source : ''
    const target = typeof item.target === 'string' ? item.target : ''
    if (!id || edgeIds.has(id) || !source || !target) return null
    if (!nodeIds.has(source) || !nodeIds.has(target)) return null
    edgeIds.add(id)

    const data = isRecord(item.data) ? item.data : {}
    const pathType =
      data.pathType === 'bezier' ||
      data.pathType === 'step' ||
      data.pathType === 'straight' ||
      data.pathType === 'smoothstep'
        ? data.pathType
        : 'smoothstep'

    edges.push({
      id,
      source,
      target,
      sourceHandle:
        typeof item.sourceHandle === 'string' ? item.sourceHandle : undefined,
      targetHandle:
        typeof item.targetHandle === 'string' ? item.targetHandle : undefined,
      type: typeof item.type === 'string' ? item.type : 'labeled',
      markerEnd: item.markerEnd as AppEdge['markerEnd'],
      data: {
        label: typeof data.label === 'string' ? data.label : '',
        pathType,
      },
    })
  }

  let viewport: QualityFlowchartGraph['viewport'] = null
  if (isRecord(raw.viewport)) {
    const { x, y, zoom } = raw.viewport
    if (isFiniteNumber(x) && isFiniteNumber(y) && isFiniteNumber(zoom)) {
      viewport = { x, y, zoom }
    }
  }

  const edgeType =
    raw.edgeType === 'bezier' ||
    raw.edgeType === 'step' ||
    raw.edgeType === 'straight' ||
    raw.edgeType === 'smoothstep'
      ? raw.edgeType
      : 'smoothstep'

  return { nodes, edges, viewport, edgeType }
}
